import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { videoCategories, videos } from "@/mocks/videos";

const durationOptions = ["全部时长", "15 分钟以内", "16–25 分钟", "26–40 分钟", "40 分钟以上"];
const dateOptions = ["全部时间", "近一个月", "近三个月", "近半年"];
const sortOptions = ["默认推荐", "最新上传", "最多播放"];

const NOW = new Date("2026-10-19T00:00:00").getTime();
const DAY = 24 * 60 * 60 * 1000;

function withinDays(date: string, days: number) {
  return NOW - new Date(`${date}T00:00:00`).getTime() <= days * DAY;
}

export default function VideoCategoryPage() {
  usePageMeta({
    title: "视频分类 - 成人网",
    description:
      "成人网视频分类页，按剧情、情侣、唯美、欧美、日韩与独立制片等类别整理演示视频，支持按播放时长、上传时间与热度筛选排序。",
    keywords: "成人视频分类,成人视频,剧情视频,唯美视频,独立制片,成人网",
  });

  const [params] = useSearchParams();
  const initialCategory = params.get("cat") ?? "全部";

  const [category, setCategory] = useState(
    videoCategories.includes(initialCategory) ? initialCategory : "全部",
  );
  const [duration, setDuration] = useState(durationOptions[0]);
  const [dateRange, setDateRange] = useState(dateOptions[0]);
  const [sort, setSort] = useState(sortOptions[0]);
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    const list = videos.filter((video) => {
      if (category !== "全部" && video.category !== category) {
        return false;
      }
      if (duration === "15 分钟以内" && video.durationMinutes > 15) {
        return false;
      }
      if (
        duration === "16–25 分钟" &&
        (video.durationMinutes < 16 || video.durationMinutes > 25)
      ) {
        return false;
      }
      if (
        duration === "26–40 分钟" &&
        (video.durationMinutes < 26 || video.durationMinutes > 40)
      ) {
        return false;
      }
      if (duration === "40 分钟以上" && video.durationMinutes <= 40) {
        return false;
      }
      if (dateRange === "近一个月" && !withinDays(video.uploadDate, 30)) {
        return false;
      }
      if (dateRange === "近三个月" && !withinDays(video.uploadDate, 92)) {
        return false;
      }
      if (dateRange === "近半年" && !withinDays(video.uploadDate, 183)) {
        return false;
      }
      return true;
    });

    if (sort === "最新上传") {
      return [...list].sort((a, b) => (a.uploadDate < b.uploadDate ? 1 : -1));
    }
    if (sort === "最多播放") {
      return [...list].sort((a, b) => b.views - a.views);
    }
    return list;
  }, [category, duration, dateRange, sort]);

  const perPage = 6;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const resetFilters = () => {
    setCategory("全部");
    setDuration(durationOptions[0]);
    setDateRange(dateOptions[0]);
    setSort(sortOptions[0]);
    setPage(1);
  };

  const filterBody = (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-foreground-500">类别</p>
        <div className="flex flex-wrap gap-1.5">
          {videoCategories.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setCategory(item);
                setPage(1);
              }}
              className={`rounded-full px-3 py-1 text-[11px] transition-colors cursor-pointer ${
                category === item
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-200 text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-foreground-500">播放时长</p>
        <div className="flex flex-wrap gap-1.5">
          {durationOptions.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setDuration(item);
                setPage(1);
              }}
              className={`rounded-full px-3 py-1 text-[11px] transition-colors cursor-pointer ${
                duration === item
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-200 text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-foreground-500">上传时间</p>
        <div className="flex flex-wrap gap-1.5">
          {dateOptions.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setDateRange(item);
                setPage(1);
              }}
              className={`rounded-full px-3 py-1 text-[11px] transition-colors cursor-pointer ${
                dateRange === item
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-200 text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={resetFilters}
        className="flex w-full items-center justify-center gap-1.5 rounded-md border border-background-300 py-2 text-[11px] text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500 whitespace-nowrap cursor-pointer"
      >
        <i className="ri-refresh-line"></i>
        重置全部筛选
      </button>
    </div>
  );

  return (
    <div className="container-page py-7">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "视频分类" }]} />

      <div className="mb-7 flex flex-col gap-4 border-b border-background-200 pb-6 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-accent-500">Videos</span>
          <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-[32px]">
            视频分类
          </h1>
          <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-foreground-500">
            按类别、时长与上传时间筛选，找到适合当下心情的作品。
            所有视频均为演示条目，不提供真实播放。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-foreground-500">共 {filtered.length} 条</span>
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {sortOptions.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setSort(item);
                  setPage(1);
                }}
                className={`rounded-full px-2.5 py-1 text-[11px] transition-colors cursor-pointer whitespace-nowrap ${
                  sort === item
                    ? "bg-accent-500 text-background-50"
                    : "text-foreground-500 hover:text-foreground-900"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="hidden lg:block">
          <div className="panel sticky top-24 p-4">{filterBody}</div>
        </aside>

        <div className="min-w-0">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="mb-4 flex w-full items-center justify-between rounded-md border border-background-300 px-4 py-2.5 text-xs text-foreground-600 lg:hidden cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-accent-500"></i>
              筛选与排序
            </span>
            <span className="flex items-center gap-1 text-foreground-500">
              共 {filtered.length} 条
              <i className="ri-arrow-down-s-line"></i>
            </span>
          </button>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {pageItems.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              title="暂无符合条件的视频"
              description="当前筛选组合下还没有内容，试着放宽时长或时间范围，或者一键重置筛选。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          )}
        </div>
      </div>

      {filtersOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-background-950/70 animate-fade-in"
            onClick={() => setFiltersOpen(false)}
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-xl border-t border-background-200 bg-background-50 p-5 animate-fade-up">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-medium text-foreground-950">筛选与排序</h2>
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-500 cursor-pointer"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="mb-5">
              <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-foreground-500">
                排序
              </p>
              <div className="flex gap-1.5">
                {sortOptions.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => {
                      setSort(item);
                      setPage(1);
                    }}
                    className={`flex-1 rounded-md px-2 py-2 text-[11px] transition-colors cursor-pointer ${
                      sort === item
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-200 text-foreground-600"
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            {filterBody}
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="mt-5 w-full rounded-md bg-primary-500 py-2.5 text-sm text-background-50 whitespace-nowrap cursor-pointer"
            >
              查看 {filtered.length} 条结果
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}